import React from 'react';
import { useSearchParams } from 'react-router-dom';
import PlannerSeason from './PlannerSeason';
import PlannerWeek from './PlannerWeek';
import { PLANNER_CSS, isoWeekOf, localToday, parseWeekParam, weekParam } from './plannerShared';
import { useUrlParam } from '../../hooks/useUrlParam';

// Club Planner (staging only) — replaces the trainer's hand-made weekly PDF. Season view (fixtures
// from the feed, +/− day labels, the long-term training / off plan) and week view (the time grid
// of blocks). Access is checked server-side on every planner endpoint (services/planner/routes.js);
// the nav entry only decides whether the tab shows. View and week live in the URL
// (?page=planner&view=week&week=2026-W42) so a week can be linked to.
export default function Planner({ user }) {
  const [view, setView] = useUrlParam('view', 'season');
  const current = isoWeekOf(localToday());
  const [weekStr] = useUrlParam('week', weekParam(current.isoYear, current.isoWeek));
  const week = parseWeekParam(weekStr) || current;
  const [fill, setFill] = useUrlParam('fill', '');
  // Whose schedule the week view shows: '' = the team, or a player id (an individual schedule).
  const [who, setWho] = useUrlParam('who', '');
  const clearFill = React.useCallback(() => setFill(''), [setFill]);
  const token = user?.sessionToken;

  // One URL update for both params: two separate useUrlParam setters in a row each start from the
  // same old URL, so the second one wiped the first (the week jumped back to the current one).
  const [, setSearchParams] = useSearchParams();
  // whoNext (optional): also switch whose schedule is shown — 'team' or a player id.
  const openWeek = (isoYear, isoWeek, whoNext) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('week', weekParam(isoYear, isoWeek));
      next.set('view', 'week');
      if (whoNext === 'team') next.delete('who');
      else if (whoNext != null) next.set('who', String(whoNext));
      return next;
    }, { replace: true });
  };

  return (
    <div className="planner-root" style={{ maxWidth: 1500, margin: '0 auto', paddingBottom: 40 }}>
      <style>{PLANNER_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Club Planner</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            {view === 'week'
              ? 'Week schedule: who is where, when. Staff see everything; players will only see what concerns them.'
              : 'Season plan: game, training and off days. Fixtures come in from the online fixture feed.'}
          </div>
        </div>
        <div role="group" aria-label="View" style={{ display: 'flex', gap: 4 }}>
          <button className={`pl-btn ${view !== 'week' ? 'pl-btn--active' : ''}`} aria-pressed={view !== 'week'} onClick={() => setView('season')}>Season</button>
          <button className={`pl-btn ${view === 'week' ? 'pl-btn--active' : ''}`} aria-pressed={view === 'week'} onClick={() => setView('week')}>Week</button>
        </div>
      </div>
      {view === 'week'
        ? <PlannerWeek token={token} isoYear={week.isoYear} isoWeek={week.isoWeek} onChangeWeek={openWeek} autoFill={fill === '1'} onAutoFillDone={clearFill}
            view={who || 'team'} onChangeView={v => setWho(v === 'team' ? '' : v)} />
        : <PlannerSeason token={token} onOpenWeek={openWeek} />}
    </div>
  );
}
